import React from 'react';
import { motion } from 'framer-motion';
interface SlideProps {
  index: number;
  total: number;
}
// Shared Hollywood-model tension curve in a 1200 x 420 viewBox.
// Impact spike → dip → Point of no return → dip → Climax peak → Resolution descent.
const CURVE = 'M 60 300 ' + 'C 70 250 110 250 120 285 ' + 'C 130 320 160 330 200 322 ' + 'C 280 305 330 300 380 250 ' + 'C 430 300 470 322 560 322 ' + 'C 660 322 720 230 780 150 ' + 'C 820 100 860 100 900 130 ' + 'C 970 180 1010 350 1120 398';
interface CurveChartProps {
  showLabels: boolean;
  strokeWidth: number;
  delay: number;
}
function CurveChart({
  showLabels,
  strokeWidth,
  delay
}: CurveChartProps) {
  const beats = [{
    label: 'Impact',
    x: 118,
    y: 285,
    anchor: 'middle' as const
  }, {
    label: 'Point of no return',
    x: 380,
    y: 250,
    anchor: 'middle' as const
  }, {
    label: 'Climax',
    x: 880,
    y: 122,
    anchor: 'middle' as const
  }, {
    label: 'Resolution',
    x: 1110,
    y: 360,
    anchor: 'end' as const
  }];
  return <svg viewBox="0 0 1200 420" preserveAspectRatio="xMidYMid meet" className="w-full h-full max-h-full" role="img" aria-label="Hollywood model narrative tension curve" data-id="element-133">
      <line x1="40" y1="20" x2="40" y2="400" stroke="#C9C5BC" strokeWidth="2" data-id="element-134" />
      <line x1="40" y1="400" x2="1160" y2="400" stroke="#C9C5BC" strokeWidth="2" data-id="element-135" />

      <motion.path d={`${CURVE} L 1120 400 L 60 400 Z`} fill="var(--brand-green)" initial={{
      opacity: 0
    }} animate={{
      opacity: 0.1
    }} transition={{
      delay: delay + 0.8,
      duration: 0.7
    }} data-id="element-136" />
      <motion.path d={CURVE} fill="none" stroke="var(--brand-green)" strokeWidth={strokeWidth} strokeLinecap="round" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      delay,
      duration: 1.2,
      ease: 'easeInOut'
    }} data-id="element-137" />
      {showLabels && beats.map((b, i) => <motion.text key={b.label} x={b.x} y={b.y} dy={-14} textAnchor={b.anchor} className="fill-[var(--fg)] font-semibold" fontSize="20" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: delay + 0.9 + i * 0.12,
      duration: 0.4
    }} data-id="element-138">
            {b.label}
          </motion.text>)}
    </svg>;
}
export function Slide05Hollywood({
  index
}: SlideProps) {
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] flex flex-col p-[clamp(1.5rem,3.5vw,3.5rem)]" data-id="element-139">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(0.75rem,1.5vw,1.5rem)]" data-id="element-140">
        <p className="text-[var(--brand-green)] text-[clamp(0.8rem,1.4vw,1.05rem)] font-semibold uppercase tracking-wider mb-[clamp(0.35rem,0.8vw,0.75rem)]" data-id="element-141">
          The Storytelling Framework
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.5rem,3.2vw,2.75rem)] leading-[1.1]" data-id="element-142">
          Built on the{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-143">
            Hollywood Model
          </span>{' '}
          of narrative tension.
        </h2>
      </motion.div>

      {/* Overall model */}
      <div className="flex-[1.4] min-h-0 flex flex-col" data-id="element-144">
        <div className="flex items-center gap-3 mb-[clamp(0.25rem,0.6vw,0.5rem)]" data-id="element-145">
          <span className="text-[var(--muted)] text-[clamp(0.7rem,1vw,0.875rem)] font-semibold uppercase tracking-widest" data-id="element-146">
            Tension
          </span>
          <span className="text-[var(--fg)] text-[clamp(0.9rem,1.4vw,1.25rem)] font-bold" data-id="element-147">
            The overall series arc
          </span>
        </div>
        <div className="flex-1 min-h-0" data-id="element-148">
          <CurveChart showLabels strokeWidth={4} delay={0.4} data-id="element-149" />
        </div>
      </div>

      {/* Episodical model */}
      <div className="flex-1 min-h-0 flex flex-col mt-[clamp(0.5rem,1.5vw,1.25rem)]" data-id="element-150">
        <p className="text-[var(--fg)] text-[clamp(0.85rem,1.3vw,1.15rem)] font-semibold mb-[clamp(0.25rem,0.6vw,0.5rem)]" data-id="element-151">
          And every episode follows the same arc.
        </p>
        <div className="flex-1 min-h-0 grid grid-cols-3 gap-[clamp(0.75rem,2vw,2rem)]" data-id="element-152">
          {['Episode I', 'Episode II', 'Episode III'].map((ep, i) => <motion.div key={ep} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.6 + i * 0.15,
          duration: 0.5
        }} className="flex flex-col min-h-0" data-id="element-153">
              <div className="flex-1 min-h-0" data-id="element-154">
                <CurveChart showLabels={false} strokeWidth={5} delay={1.6 + i * 0.15} data-id="element-155" />
              </div>
              <span className="text-center text-[var(--brand-green)] font-bold text-[clamp(0.8rem,1.2vw,1.05rem)] mt-1" data-id="element-156">
                {ep}
              </span>
            </motion.div>)}
        </div>
      </div>

      {/* Bottom statement */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2.2,
      duration: 0.6
    }} className="flex justify-between items-end mt-[clamp(0.5rem,1.2vw,1rem)]" data-id="element-157">
        <p className="text-[var(--fg)] text-[clamp(0.95rem,1.6vw,1.4rem)] font-semibold" data-id="element-158">
          Anticipation, payoff, and resolution — at every scale.
        </p>
        <span className="text-[var(--muted)] text-sm" data-id="element-159">{index + 1}</span>
      </motion.div>
    </div>;
}